import { Box, Button, Grid2 as Grid, Link, Stack, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSupplier } from "../../hooks/useSupplier";
import { PageContext } from "../../context/PageContext";
import LoadingPage from "../../components/LoadingPage";
import { ExternalLink } from "../../components/ExternalLink";
import { useReadingEmail } from "../../hooks/useReadingEmail";
import { useContacts } from "../../hooks/useContacts";
import { ContactAlert } from "../../components/ContactAlert";
import { History } from "../../components/History";
import { usePayable } from "../../hooks/usePayable";
import { Tip } from "../../components/Tip";
import { useRedirectToActivities } from "../../hooks/useRedirectToActivities";
import { ContactInfo } from "./ContactInfo";
import { ContactNotFound } from "./ContactNotFound";
import { InvoiceInformation } from "./InvoiceInformation";
import { RemittanceComplete } from "./RemittanceComplete";

import { WithFooter } from "./WithFooter";
import { Footer } from "../../components/Footer";
import ActionWithAi from "../../components/ActionWithAi";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSuggestedActions } from "../../hooks/useSuggestedActions";
import InvoiceComments from "../../components/InvoiceComments/InvoiceComments";

const PayableInvoiceDetail = () => {
  const { actions } = useSuggestedActions();
  const { supplierId, invoiceId } = useParams<{ supplierId: string; invoiceId: string }>();
  const { payable, ...payableResponse } = usePayable(invoiceId);
  const { supplier, ...supplierResponse } = useSupplier(supplierId);
  const { emailAddress } = useReadingEmail();
  const { contacts, ...contactsResponse } = useContacts(emailAddress);
  const isSaved = contacts?.find(({ id }) => id === supplierId)?.isSaved ?? false;
  const invoiceNumber = payable?.invoiceDetails?.invoiceNumber;
  const [completed, setCompleted] = useState(false);

  const navigate = useNavigate();

  const { setNewTitle } = useContext(PageContext);

  useEffect(() => {
    setNewTitle(invoiceNumber ? `Payable ${invoiceNumber}` : "Payables");
  }, [setNewTitle, invoiceNumber]);

  useRedirectToActivities(emailAddress);

  const onBackClick = () => {
    navigate(`/payables/${supplierId}`);
  };

  if (
    payableResponse.isLoading ||
    contactsResponse.isLoading ||
    supplierResponse.isLoading ||
    payableResponse.isError ||
    contactsResponse.isError ||
    supplierResponse.isError
  ) {
    return (
      <WithFooter>
        <LoadingPage />;
      </WithFooter>
    );
  }

  if (!supplier) {
    return (
      <WithFooter>
        <ContactNotFound email={emailAddress ?? ""} />;
      </WithFooter>
    );
  }

  if (!payable) {
    return (
      <WithFooter>
        <Tip title="Invoice not found" />;
      </WithFooter>
    );
  }

  if (completed) {
    return (
      <WithFooter>
        <RemittanceComplete />
      </WithFooter>
    );
  }

  return (
    <>
      <Stack spacing={2} pb={8}>
        <ContactInfo
          title="Supplier"
          contactLink={supplier.link}
          companyName={supplier.companyName}
          accountNumber={supplier.accountNumber}
          status={{ status: payable.invoiceDetails.status, dueDate: payable.invoiceDetails.dueDate ?? "" }}
        />
        {emailAddress && (
          <ContactAlert
            isSaved={isSaved}
            email={emailAddress}
            companyName={supplier.companyName}
            contactLink={supplier.link}
            integration={payable.payableSource}
            accountNumber={supplier.accountNumber}
          />
        )}
        <Box>
          <Typography variant="h2" sx={{ lineHeight: "18px" }}>
            Invoice
          </Typography>
          <ExternalLink href={payable.invoiceDetails.link}>{invoiceNumber}</ExternalLink>
        </Box>
        <InvoiceInformation
          invoice={payable.invoiceDetails}
          companyName={supplier.companyName}
          supplierLink={supplier.link}
          accountNumber={supplier.accountNumber}
        />
        <InvoiceComments invoiceId={invoiceId} onCommentAdded={payableResponse.refetchPayable} />
        <History title="History" emptyText="No history" history={payable.payableHistory} />
      </Stack>
      <Footer>
        <Grid size="grow">
          <Button variant="outlined" fullWidth={true} startIcon={<ArrowBackIcon />} onClick={onBackClick}>
            Back
          </Button>
        </Grid>
        <Grid size="grow">
          <ActionWithAi actions={actions} relatedIds={[invoiceId ?? ""]} onAct={() => setCompleted(true)} />
        </Grid>
      </Footer>
    </>
  );
};

export default PayableInvoiceDetail;
